// src/services/api/clients.ts — #843 Task 1.6
//
// Frontend client voor de organisatie-clients endpoints
// (`/api/organizations/:orgId/clients` + sub-resources).

import { Client } from '../../types.ts';
import { fetchWithTimeout, parseErrorResponse } from './base.ts';

export interface ClientUsage {
  linked_projects_count: number;
}

export interface ClientExport {
  client: Client;
  linked_projects: Array<{
    id: string;
    name: string;
    status: string;
    expected_delivery_date: string | null;
    delivered_at: string | null;
    member_count: number;
    created_at: string;
  }>;
  exported_at: string;
  exported_by: { account_id: string; name: string };
}

export interface ClientCreateInput {
  name: string;
  email?: string;
  phone?: string;
  notes?: string;
  /** Server retourneert duplicate-warning bij gelijke naam tenzij `force=true`. */
  force?: boolean;
}

export interface ClientCreateResponse {
  client?: Client;
  warning?: 'duplicate_name';
  existing_id?: string;
  message?: string;
}

export const clientsApi = {
  list: async (orgId: string): Promise<Client[]> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients`);
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data.clients;
  },

  get: async (orgId: string, id: string): Promise<Client> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients/${id}`);
    if (!res.ok) await parseErrorResponse(res);
    const data = await res.json();
    return data.client;
  },

  create: async (orgId: string, input: ClientCreateInput): Promise<ClientCreateResponse> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  update: async (
    orgId: string,
    id: string,
    input: ClientCreateInput
  ): Promise<ClientCreateResponse> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  delete: async (orgId: string, id: string): Promise<void> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok && res.status !== 204) await parseErrorResponse(res);
  },

  getUsage: async (orgId: string, id: string): Promise<ClientUsage> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients/${id}/usage`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  exportData: async (orgId: string, id: string): Promise<ClientExport> => {
    const res = await fetchWithTimeout(`/api/organizations/${orgId}/clients/${id}/export`);
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
